import { action, computed, flow, makeObservable, observable, reaction } from 'mobx';
import moment from 'moment';
import api from '../utils/apiService';

import { apiFetcher } from '../utils/fetch';

const paginationSchema = {
  page: 1,
  pageSize: 50,
  totalPages: 1,
  totalData: 0,
  hasNextPage: false,
  hasPrevPage: false
}

const serializeFilterValues = (values) => {
  const list = Array.isArray(values) ? values : [values];
  if (list.length === 0 || list.includes('all') || list.includes(undefined) || list.includes(null)) return 'all';
  return list.join(',');
}

export default class Expense {
  constructor() {
    this.data = [];
    this.dataRecurring = {};
    this.dataAllFilter = {};
    this.dataChart = [];
    this.filters = { year: moment().year(), month: ['all'], propertyId: ['all'], category: ['all'], roomId: ['all'], recurringStatus: "all", search: "" };
    this.sort = {
      all: { sortBy: 'date', sortOrder: 'desc' },
      recurring: { sortBy: 'date', sortOrder: 'desc' },
    };
    this.paginationAll = paginationSchema;
    this.paginationRecurring = paginationSchema;
    this.selected = {};
    this.selectedExpense = [];
    this.previousFilters = { ...this.filters }; // Track previous filter values

    makeObservable(this, {
      data: observable,
      dataRecurring: observable,
      dataAllFilter: observable,
      dataChart: observable,
      selectedExpense: observable,
      filters: observable,
      sort: observable,
      paginationAll: observable,
      paginationRecurring: observable,
      selected: observable,
      setFilters: action,
      setSort: action,
      setSelected: action,
      setPagination: action,
      fetchWithoutProperty: flow,
      fetchRecurring: flow,
      fetchAllWithFilter: flow,
      deleteRecurring: flow,
      stopRecurring: flow,
      sameById: flow,
      delete: flow,
      create: flow,
      update: flow,
      updateAll: flow,
    });

    reaction(
      () => this.filters, // Hanya memantau perubahan pada filters
      (newFilters) => {
        // * Check which filters changed
        const changedFilters = Object.keys(newFilters).filter(
          key => JSON.stringify(newFilters[key]) !== JSON.stringify(this.previousFilters[key])
        );

        // * Check if only recurringStatus changed
        const onlyRecurringStatusChanged =
          changedFilters.length === 1 &&
          changedFilters[0] === 'recurringStatus';

        if (onlyRecurringStatusChanged) {
          // * Only fetch recurring if only recurringStatus changed
          this.fetchRecurring();
        } else {
          // * Otherwise, fetch all three
          this.fetchWithoutProperty();
          this.fetchRecurring();
          this.fetchAllWithFilter();
        }

        // * Update previousFilters for next comparison
        this.previousFilters = { ...newFilters };
      }
    );
  }

  setSelected = (expense) => (this.selected = expense);
  setSelectedExpense = (expense) => (this.selectedExpense = expense);

  setFilters = ({ year = "", month = "", propertyId = "", category = "", roomId = "", recurringStatus = "", search = "" }) =>
    (this.filters = { year, month, propertyId, category, roomId, recurringStatus, search });

  setSort = ({ type = "all", sortBy = "date", sortOrder = "desc" }) =>
    (this.sort = { ...this.sort, [type]: { sortBy, sortOrder } });

  setPagination = ({ page, limit, type = "all" }) => {
    const paginationKey = type === "all" ? "paginationAll" : "paginationRecurring";
    const current = this[paginationKey];
    this[paginationKey] = {
      ...current,
      page: page || current.page,
      pageSize: limit || current.pageSize,
    };
  };

  *fetchWithoutProperty() {
    try {
      const response = yield apiFetcher().get(`/expense/${this.filters.year}`);
      this.data = response.data;

      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *fetchRecurring() {
    try {
      const { year, month, propertyId, category, roomId, recurringStatus, search } = this.filters;

      const response = yield apiFetcher().get(`/expense/recurring/${year}`, {
        params: {
          page: this.paginationRecurring.page,
          limit: this.paginationRecurring.pageSize,
          propertyId: serializeFilterValues(propertyId),
          category: serializeFilterValues(category),
          month: serializeFilterValues(month),
          roomId: serializeFilterValues(roomId),
          status: recurringStatus,
          sortBy: this.sort.recurring.sortBy,
          sortOrder: this.sort.recurring.sortOrder,
          search
        },
      });

      const result = response?.data;

      this.dataRecurring = result.data || [];
      this.paginationRecurring = {
        ...this.paginationRecurring,
        totalPages: result.pagination.pages,
        totalData: result.pagination.total,
        hasNextPage: response.data.pagination.hasNextPage,
        hasPrevPage: response.data.pagination.hasPrevPage,
      }

      return { status: 200, data: result };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }


  *fetchAllWithFilter() {
    try {
      const { year, month, propertyId, category, roomId, recurringStatus, search } = this.filters;

      const response = yield apiFetcher().get(`/expense/filter/${year}`, {
        params: {
          page: this.paginationAll.page,
          limit: this.paginationAll.pageSize,
          propertyId: serializeFilterValues(propertyId),
          category: serializeFilterValues(category),
          month: serializeFilterValues(month),
          roomId: serializeFilterValues(roomId),
          status: recurringStatus,
          sortBy: this.sort.all.sortBy,
          sortOrder: this.sort.all.sortOrder,
          search
        },
      });

      const result = response?.data;

      this.dataAllFilter = result.data || [];
      this.dataChart = result.chart || [];
      this.paginationAll = {
        ...this.paginationAll,
        totalPages: result.pagination.pages,
        totalData: result.pagination.total,
        hasNextPage: response.data.pagination.hasNextPage,
        hasPrevPage: response.data.pagination.hasPrevPage,
      }

      return { status: 200, data: result };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *create(expense) {
    try {
      const response = yield apiFetcher().post(`/expense`, expense);

      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *update(id, expense) {
    try {
      const response = yield apiFetcher().patch(`/expense/${id}`, expense);

      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *updateAll(id, expense) {
    try {
      const response = yield apiFetcher().patch(`/expense/recurring/${id}`, expense);

      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *delete(id) {
    try {
      const response = yield apiFetcher().delete(`/expense/${id}`);
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *deleteRecurring(id) {
    try {
      const response = yield apiFetcher().delete(`/expense/deleteReccuring/${id}`);
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *stopRecurring(id) {
    try {
      const response = yield apiFetcher().patch(`/expense/stop/${id}`);
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *sameById(id) {
    try {
      const response = yield apiFetcher().get(`/expense/sameById/${id}`);
      if (Array.isArray(response.data)) {
        this.setSelectedExpense(response.data);
      } else {
        console.warn("Expected array but received:", response.data);
        this.setSelectedExpense([]);
      }
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }
}
